import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { projectQueryOptions } from '@/features/projects/projects-queries';
import { prototypeQueryOptions } from '@/features/prototypes/prototypes-queries';

/** The crumb every trail starts from. Its target is the only literal link. */
const HOME_LABEL = 'Projects';

/**
 * Generic label shown for the split second before the entity's name resolves
 * (or if the fetch fails), keyed by which route param is present.
 */
const FALLBACK_PROJECT_LABEL = 'Project';
const FALLBACK_PROTOTYPE_LABEL = 'Prototype';

/**
 * The breadcrumb region of the application shell.
 *
 * Derived from the router's own params rather than from props, so a page cannot
 * render a trail that disagrees with where the user actually is. The leaf crumb
 * names the actual project/prototype, read from the same React Query cache the
 * detail route already populated (so it is usually resolved instantly); the
 * generic label is only a fallback while it loads. The final crumb is the
 * current page and is deliberately not a link.
 */
export const Breadcrumbs = () => {
  // `strict: false`: this lives in the shell, above any single route, so it
  // reads whichever of these params the active route happens to carry.
  const params = useParams({ strict: false }) as {
    projectId?: string;
    prototypeId?: string;
  };
  const { projectId, prototypeId } = params;

  const projectQuery = useQuery({
    ...projectQueryOptions(projectId ?? ''),
    enabled: projectId !== undefined,
  });
  const prototypeQuery = useQuery({
    ...prototypeQueryOptions(prototypeId ?? ''),
    enabled: prototypeId !== undefined,
  });

  let leafLabel: string | null = null;
  if (prototypeId !== undefined) {
    leafLabel = prototypeQuery.data?.name ?? FALLBACK_PROTOTYPE_LABEL;
  } else if (projectId !== undefined) {
    leafLabel = projectQuery.data?.name ?? FALLBACK_PROJECT_LABEL;
  }

  const trail = [
    { key: 'home', label: HOME_LABEL, href: '/' as const },
    ...(leafLabel === null
      ? []
      : [{ key: 'leaf', label: leafLabel, href: null }]),
  ];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;

          return (
            <Fragment key={crumb.key}>
              <BreadcrumbItem>
                {isLast || crumb.href === null ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {isLast ? null : <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
